<script setup lang="ts">
import { computed } from 'vue';

export interface ButtonProps {
    text?: string;
    type?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger' | 'link' | 'inherit';
    icon?: string;
    disabled?: boolean;
    size?: 'normal' | 'small' | 'large',
    shape?: 'normal' | 'flat' | 'round' | 'outline';
    block?: boolean;
    tooltip?: string;
    loading?: boolean;
    iconColor?: string;
    active?: boolean;
    actionType?: "button" | "submit" | "reset"
}

const props = withDefaults(defineProps<ButtonProps>(), {
    text: '',
    type: 'secondary',
    disabled: false,
    size: 'normal',
    shape: 'normal',
    block: false,
    active: false,
    actionType: 'button'
});

const emit = defineEmits(['click']);

const buttonClass = computed(() => {
    let cssClass = props.disabled ? '' : `e-${props.type}`;

    if (props.size != 'normal') {
        cssClass += ` e-${props.size}`
    }

    if (props.shape != 'normal') {
        cssClass += ` e-${props.shape}`
    }

    if (props.block) {
        cssClass += ' e-block'
    }

    if (props.loading) {
        cssClass += ' e-loading'
    }

    if (props.active) {
        cssClass += ' e-active'
    }

    return cssClass
});

const onClick = (event: Event) => {
    event.preventDefault();
    event.stopPropagation()
    if (!props.disabled && !props.loading) {
        emit('click', event);
    }
}

</script>

<template>

    <button :type="actionType" :class="'e-btn ' + buttonClass" @click="onClick" :title="tooltip" :disabled="disabled">
        <div v-if="loading" class="loader"></div>

        <i v-if="icon" :class="icon" :style="iconColor && { color: iconColor }"></i>
        {{ text }}
    </button>
</template>


<style scoped>
button {
    white-space: normal;
    word-break: break-word;
}

.e-block {
    white-space: normal;
}

.e-large {
    height: 40px;
    font-size: 15px;
}

.loader {
    width: 14px;
    height: 14px;
    border: 1px solid #FFF;
    border-bottom-color: var(--color-sf-primary);
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
}

@keyframes rotation {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.e-loading {
    background: var(--color-sf-primary-bg-color-hover) !important;
    border-color: var(--color-sf-primary-border-color-hover) !important;
}
</style>